import { Type } from "@sinclair/typebox";
import { ModelCacheUsageQuerySchema, ModelCacheUsageSnapshotSchema, } from "./schemas/model-cache.js";
import { DemoCaseResponseSchema, DemoIdSchema, DemoSourceSchema, ContinueDemoBodySchema, } from "./schemas/demo.js";
import { FileRefSchema, FileVersionSchema } from "./schemas/files.js";
import { TerminationSchema } from "./schemas/lifecycle.js";
import { ActionGrantSchema, ActionRequestSchema, ObservationSchema, AuthorizationEnvelopeSchema, } from "./schemas/action.js";
import { GuideReviewEvidenceSchema, RecordGuideCanaryBodySchema, GuideCanaryEvidenceSchema, GuideApprovalSchema, GuideDefinitionSchema, GuideFunctionDigestsSchema, } from "./schemas/guide.js";
import { ModelProxyRequestSchema, ModelProxyStorageFailureSchema, ModelProxyExpiredSchema, } from "./schemas/model.js";
import { ProviderSearchProxyRequestSchema, ProviderSearchProxyResponseSchema, } from "./schemas/provider-search.js";
import { IdSchema, NonNegativeIntegerSchema, Sha256Schema, TimestampSchema, } from "./schemas/shared.js";
import { TaskEventSchema, WorkingPlanSchema } from "./schemas/task.js";
import { ResultDocumentSchema } from "./schemas/result-document.js";
import { GuideRunnerCredentialSchema, GuideRunnerSchema, GuideRunnerTokenBodySchema, GuideRunnerTokenSchema, } from "./schemas/guide-runner.js";
import { ReportSourceSchema } from "./schemas/report.js";
import { GuideReviewSummaryListSchema, GuideReviewFindingDecisionSchema, GuideReviewRetryResultSchema, } from "./schemas/guide-review.js";
export const SessionReportSchema = Type.Object({
    reportId: IdSchema,
    sessionId: IdSchema,
    revision: Type.Integer({ minimum: 1 }),
    attemptTurnId: IdSchema,
    sourceTurnId: IdSchema,
    evidenceDigest: Sha256Schema,
    source: ReportSourceSchema,
    status: Type.Union([
        Type.Literal("pending"),
        Type.Literal("running"),
        Type.Literal("approved"),
        Type.Literal("failed"),
        Type.Literal("cancelled"),
        Type.Literal("stale"),
    ]),
    document: Type.Union([ResultDocumentSchema, Type.Null()]),
}, { additionalProperties: false });
export const RetryReportBodySchema = Type.Object({
    requestId: IdSchema,
    expectedRevision: Type.Integer({ minimum: 1 }),
    evidenceDigest: Sha256Schema,
}, { additionalProperties: false });
export const UserGenderSchema = Type.Union([
    Type.Literal("female"),
    Type.Literal("male"),
    Type.Literal("other"),
    Type.Literal("undisclosed"),
]);
export const UserStatusSchema = Type.Union([
    Type.Literal("pending"),
    Type.Literal("active"),
    Type.Literal("disabled"),
]);
export const PublicUserSchema = Type.Object({
    userId: IdSchema,
    email: Type.String({ format: "email", maxLength: 254 }),
    name: Type.Union([Type.String({ minLength: 1, maxLength: 100 }), Type.Null()]),
    institution: Type.Union([Type.String({ minLength: 1, maxLength: 200 }), Type.Null()]),
    gender: UserGenderSchema,
    status: UserStatusSchema,
    role: Type.Union([Type.Literal("admin"), Type.Literal("user")]),
    allowRawArtifacts: Type.Boolean(),
    mustRotateCredentials: Type.Boolean(),
    createdAt: TimestampSchema,
}, { additionalProperties: false });
export const RegisterBodySchema = Type.Object({
    name: Type.String({ minLength: 1, maxLength: 100, pattern: "\\S" }),
    institution: Type.String({ minLength: 1, maxLength: 200, pattern: "\\S" }),
    gender: Type.Optional(UserGenderSchema),
    email: Type.String({ minLength: 3, maxLength: 254 }),
    password: Type.String({ minLength: 12, maxLength: 128 }),
    allowRawArtifacts: Type.Optional(Type.Boolean({ default: false })),
}, { additionalProperties: false });
export const LoginBodySchema = Type.Object({
    email: Type.String({ minLength: 3, maxLength: 254 }),
    password: Type.String({ minLength: 1, maxLength: 128 }),
}, { additionalProperties: false });
/** 运行器文件合同：人工账号登录与限定验收委托是两种显式闭合格式。 */
export const GuideCanaryIdentityCredentialSchema = Type.Union([
    LoginBodySchema,
    GuideRunnerCredentialSchema,
]);
export const AuthOptionsSchema = Type.Object({
    registrationEnabled: Type.Boolean(),
    passwordMinLength: Type.Integer({ minimum: 12, maximum: 128 }),
    passwordMaxLength: Type.Integer({ minimum: 12, maximum: 128 }),
}, { additionalProperties: false });
export const CompleteBootstrapBodySchema = Type.Object({
    newPassword: Type.String({ minLength: 12, maxLength: 128 }),
    allowRawArtifacts: Type.Boolean(),
}, { additionalProperties: false });
export const UpdateRawArtifactConsentBodySchema = Type.Object({ allowRawArtifacts: Type.Boolean() }, { additionalProperties: false });
export const ChangeOwnPasswordBodySchema = Type.Object({
    currentPassword: Type.String({ minLength: 1, maxLength: 128 }),
    newPassword: Type.String({ minLength: 12, maxLength: 128 }),
}, { additionalProperties: false });
export const AdminUserSchema = Type.Object({
    ...PublicUserSchema.properties,
}, { additionalProperties: false });
export const AdminUserListSchema = Type.Array(AdminUserSchema);
export const AdminResetUserPasswordBodySchema = Type.Object({ password: Type.String({ minLength: 12, maxLength: 128 }) }, { additionalProperties: false });
export const ModelUsageSummarySchema = Type.Object({
    modelId: Sha256Schema,
    modelDisplayName: Type.String({ minLength: 1, maxLength: 255 }),
    calls: NonNegativeIntegerSchema,
    inputTokens: NonNegativeIntegerSchema,
    outputTokens: NonNegativeIntegerSchema,
    totalTokens: NonNegativeIntegerSchema,
    unreportedCalls: NonNegativeIntegerSchema,
    firstUsedAt: TimestampSchema,
    lastUsedAt: TimestampSchema,
}, { additionalProperties: false });
export const ToolUsageSummarySchema = Type.Object({
    serverId: Type.String({ minLength: 1, maxLength: 255 }),
    functionName: Type.String({ minLength: 1, maxLength: 256 }),
    calls: NonNegativeIntegerSchema,
    succeededCalls: NonNegativeIntegerSchema,
    failedCalls: NonNegativeIntegerSchema,
    firstUsedAt: TimestampSchema,
    lastUsedAt: TimestampSchema,
}, { additionalProperties: false });
export const AdminUserUsageSchema = Type.Object({
    userId: IdSchema,
    email: Type.String({ format: "email", maxLength: 254 }),
    settledActions: NonNegativeIntegerSchema,
    cpuMillis: NonNegativeIntegerSchema,
    memoryBytes: NonNegativeIntegerSchema,
    inputBytes: NonNegativeIntegerSchema,
    outputBytes: NonNegativeIntegerSchema,
    externalCalls: NonNegativeIntegerSchema,
    totalModelCalls: NonNegativeIntegerSchema,
    totalInputTokens: NonNegativeIntegerSchema,
    totalOutputTokens: NonNegativeIntegerSchema,
    totalTokens: NonNegativeIntegerSchema,
    unreportedModelCalls: NonNegativeIntegerSchema,
    totalToolCalls: NonNegativeIntegerSchema,
    models: Type.Array(ModelUsageSummarySchema, { maxItems: 64 }),
    tools: Type.Array(ToolUsageSummarySchema, { maxItems: 10_000 }),
}, { additionalProperties: false });
export const AdminUsageListSchema = Type.Array(AdminUserUsageSchema);
/**
 * 阶段 7：MCP 原始用量缓存投影。
 *
 * 只用于展示与成本归因，**不是账单**：用户账单仍由平台 `usage_ledger` 权威。响应里显式带
 * `source` 与同步时间，避免界面把缓存误读成结算结果。
 */
export const McpRawUsageFunctionSchema = Type.Object({
    serviceId: IdSchema,
    functionName: Type.String({ minLength: 1, maxLength: 256 }),
    invocations: NonNegativeIntegerSchema,
    succeeded: NonNegativeIntegerSchema,
    failed: NonNegativeIntegerSchema,
    wallTimeMs: NonNegativeIntegerSchema,
    externalCalls: NonNegativeIntegerSchema,
}, { additionalProperties: false });
export const McpRawUsageSchema = Type.Object({
    source: Type.Literal("mcp_observed"),
    enabled: Type.Boolean(),
    measuredAt: TimestampSchema,
    totals: Type.Object({
        events: NonNegativeIntegerSchema,
        succeeded: NonNegativeIntegerSchema,
        failed: NonNegativeIntegerSchema,
        wallTimeMs: NonNegativeIntegerSchema,
        inputBytes: NonNegativeIntegerSchema,
        outputBytes: NonNegativeIntegerSchema,
        externalCalls: NonNegativeIntegerSchema,
    }, { additionalProperties: false }),
    functions: Type.Array(McpRawUsageFunctionSchema, { maxItems: 1_000 }),
}, { additionalProperties: false });
export const SessionUsageTotalsSchema = Type.Object({
    modelCalls: NonNegativeIntegerSchema,
    inputTokens: NonNegativeIntegerSchema,
    outputTokens: NonNegativeIntegerSchema,
    totalTokens: NonNegativeIntegerSchema,
    unreportedModelCalls: NonNegativeIntegerSchema,
    toolCalls: NonNegativeIntegerSchema,
    succeededToolCalls: NonNegativeIntegerSchema,
    failedToolCalls: NonNegativeIntegerSchema,
    providerSearchExecutions: NonNegativeIntegerSchema,
    providerSearchCalls: NonNegativeIntegerSchema,
    providerSearchInputTokens: NonNegativeIntegerSchema,
    providerSearchOutputTokens: NonNegativeIntegerSchema,
    providerSearchUnreportedCalls: NonNegativeIntegerSchema,
}, { additionalProperties: false });
export const SessionUsageProviderSearchSchema = Type.Object({
    executions: NonNegativeIntegerSchema,
    searchCalls: NonNegativeIntegerSchema,
    inputTokens: NonNegativeIntegerSchema,
    outputTokens: NonNegativeIntegerSchema,
    totalTokens: NonNegativeIntegerSchema,
    unreportedCalls: NonNegativeIntegerSchema,
}, { additionalProperties: false });
export const SessionUsageTurnSchema = Type.Object({
    turnId: IdSchema,
    status: Type.Union([
        Type.Literal("accepted"),
        Type.Literal("running"),
        Type.Literal("waiting_plan"),
        Type.Literal("waiting_approval"),
        Type.Literal("completed"),
        Type.Literal("failed"),
        Type.Literal("cancelled"),
    ]),
    createdAt: TimestampSchema,
    modelCalls: NonNegativeIntegerSchema,
    inputTokens: NonNegativeIntegerSchema,
    outputTokens: NonNegativeIntegerSchema,
    totalTokens: NonNegativeIntegerSchema,
    unreportedModelCalls: NonNegativeIntegerSchema,
    toolCalls: NonNegativeIntegerSchema,
    succeededToolCalls: NonNegativeIntegerSchema,
    failedToolCalls: NonNegativeIntegerSchema,
    providerSearchExecutions: NonNegativeIntegerSchema,
    providerSearchCalls: NonNegativeIntegerSchema,
}, { additionalProperties: false });
export const SessionUsageSummarySchema = Type.Object({
    sessionId: IdSchema,
    totals: SessionUsageTotalsSchema,
    models: Type.Array(ModelUsageSummarySchema, { maxItems: 64 }),
    tools: Type.Array(ToolUsageSummarySchema, { maxItems: 10_000 }),
    providerSearch: SessionUsageProviderSearchSchema,
    turns: Type.Array(SessionUsageTurnSchema, { maxItems: 10_000 }),
}, { additionalProperties: false });
export const GuideSummarySchema = Type.Object({
    definitionId: IdSchema,
    title: Type.String({ minLength: 1, maxLength: 300 }),
    contentDigest: Sha256Schema,
    matchingHints: Type.Array(Type.String({ minLength: 1, maxLength: 500 }), { maxItems: 64 }),
    limitations: Type.Array(Type.String({ minLength: 1, maxLength: 2_000 }), { maxItems: 128 }),
}, { additionalProperties: false });
export const GuideListSchema = Type.Array(GuideSummarySchema);
export const AdminGuideSchema = Type.Object({
    definition: GuideDefinitionSchema,
    effectiveStatus: GuideDefinitionSchema.properties.status,
    canaryCurrent: Type.Boolean(),
    approvalCurrent: Type.Boolean(),
    canary: Type.Union([GuideCanaryEvidenceSchema, Type.Null()]),
    approval: Type.Union([GuideApprovalSchema, Type.Null()]),
}, { additionalProperties: false });
export const AdminGuideListSchema = Type.Array(AdminGuideSchema);
export const GuideRuntimeSnapshotSchema = Type.Object({
    runtimeDigest: Sha256Schema,
    protocolDigest: Sha256Schema,
    serverId: IdSchema,
    functionDigests: Type.Optional(GuideFunctionDigestsSchema),
}, { additionalProperties: false });
export const CreateGuideCanaryTurnBodySchema = Type.Object({
    sessionId: IdSchema,
    requestId: IdSchema,
    contentDigest: Sha256Schema,
    goal: Type.String({ minLength: 1, maxLength: 100_000 }),
}, { additionalProperties: false });
export const ApproveGuideBodySchema = Type.Object({
    contentDigest: Sha256Schema,
    traceDigest: Sha256Schema,
    reviewId: IdSchema,
    reviewDigest: Sha256Schema,
    findingDecisions: Type.Array(GuideReviewFindingDecisionSchema, { maxItems: 512 }),
    reviewedGoalPlanAndTools: Type.Literal(true),
    reviewedOutputsFailuresAndBudget: Type.Literal(true),
    reviewedDisclosureAndLimitations: Type.Literal(true),
}, { additionalProperties: false });
export const GuideLifecycleResultSchema = Type.Object({
    definitionId: IdSchema,
    contentDigest: Sha256Schema,
    status: Type.Union([
        Type.Literal("draft"),
        Type.Literal("blocked"),
        Type.Literal("validated"),
        Type.Literal("approved"),
    ]),
}, { additionalProperties: false });
export const SelectedGuideSchema = Type.Object({
    definitionId: IdSchema,
    title: Type.String({ minLength: 1, maxLength: 300 }),
    contentDigest: Sha256Schema,
    matchReason: Type.String({ minLength: 1, maxLength: 1_000 }),
}, { additionalProperties: false });
export const SessionSchema = Type.Object({
    terminations: Type.Optional(Type.Array(TerminationSchema)),
    sessionId: IdSchema,
    title: Type.String({ minLength: 1, maxLength: 120 }),
    titlePending: Type.Boolean(),
    status: Type.Union([
        Type.Literal("provisioning"),
        Type.Literal("rotating"),
        Type.Literal("active"),
        Type.Literal("failed"),
        Type.Literal("deleting"),
        Type.Literal("deleted"),
    ]),
    runtimeGeneration: Type.Integer({ minimum: 0 }),
    targetDigest: Sha256Schema,
    seriesId: Type.Union([Type.String({ pattern: "^[a-z][a-z0-9_-]{0,63}$" }), Type.Null()]),
    seriesConfigDigest: Sha256Schema,
    seriesDisplayName: Type.String({ minLength: 1, maxLength: 255 }),
    createdAt: TimestampSchema,
}, { additionalProperties: false });
/** 侧边栏运行时状态：取值与事件推导出的界面状态保持一致。 */
export const SessionRuntimeStateSchema = Type.Union([
    Type.Literal("preparing"),
    Type.Literal("idle"),
    Type.Literal("planning"),
    Type.Literal("awaiting_user_input"),
    Type.Literal("awaiting_plan_start"),
    Type.Literal("awaiting_action_approval"),
    Type.Literal("executing"),
    Type.Literal("running"),
    Type.Literal("stopping"),
    Type.Literal("stop_failed"),
    Type.Literal("completed"),
    Type.Literal("failed"),
    Type.Literal("cancelled"),
]);
/**
 * 列表必须自带服务端运行时投影：客户端不得为每个会话重建完整事件历史。
 */
export const SessionListEntrySchema = Type.Composite([
    SessionSchema,
    Type.Object({
        runtimeState: SessionRuntimeStateSchema,
        activeTurnId: Type.Union([IdSchema, Type.Null()]),
        lastActivityAt: TimestampSchema,
    }, { additionalProperties: false }),
], { additionalProperties: false });
export const SessionListSchema = Type.Array(SessionListEntrySchema);
export const TaskEventListSchema = Type.Array(TaskEventSchema);
const CatalogIdentifierSchema = Type.String({ pattern: "^[a-z][a-z0-9_-]{0,63}$" });
const ReasoningEffortSchema = Type.String({ minLength: 1, maxLength: 64 });
const RuntimeReasoningCapabilitySchema = Type.Union([
    Type.Object({
        supported: Type.Literal(false),
        efforts: Type.Tuple([]),
        defaultEffort: Type.Null(),
    }, { additionalProperties: false }),
    Type.Object({
        supported: Type.Literal(true),
        efforts: Type.Array(ReasoningEffortSchema, {
            minItems: 1,
            uniqueItems: true,
        }),
        defaultEffort: ReasoningEffortSchema,
    }, { additionalProperties: false }),
]);
const RuntimeWebSearchCapabilitySchema = Type.Union([
    Type.Object({ supported: Type.Literal(false) }, { additionalProperties: false }),
    Type.Object({
        supported: Type.Literal(true),
        providerNative: Type.Literal(true),
        evidenceMode: Type.Union([Type.Literal("structured_sources"), Type.Literal("summary_only")]),
    }, { additionalProperties: false }),
]);
export const RuntimeModelOptionSchema = Type.Object({
    providerId: CatalogIdentifierSchema,
    providerDisplayName: Type.String({ minLength: 1, maxLength: 128 }),
    modelId: CatalogIdentifierSchema,
    modelConfigDigest: Sha256Schema,
    displayName: Type.String({ minLength: 1, maxLength: 128 }),
    maxInputTokens: Type.Integer({ minimum: 1, maximum: 10_000_000 }),
    maxOutputTokens: Type.Integer({ minimum: 1, maximum: 10_000_000 }),
    reasoning: RuntimeReasoningCapabilitySchema,
    webSearch: RuntimeWebSearchCapabilitySchema,
}, { additionalProperties: false });
export const RuntimeSeriesOptionSchema = Type.Object({
    seriesId: CatalogIdentifierSchema,
    displayName: Type.String({ minLength: 1, maxLength: 128 }),
    seriesConfigDigest: Sha256Schema,
    planner: RuntimeModelOptionSchema,
    worker: RuntimeModelOptionSchema,
    vision: Type.Array(RuntimeModelOptionSchema, { maxItems: 8 }),
}, { additionalProperties: false });
export const WorkspaceUploadPolicySchema = Type.Object({
    maxFileBytes: Type.Integer({ minimum: 1, maximum: 1024 * 1024 * 1024 }),
    maxInputFiles: Type.Integer({ minimum: 1, maximum: 10_000 }),
    maxInputTotalBytes: Type.Integer({ minimum: 1, maximum: 20 * 1024 * 1024 * 1024 }),
}, { additionalProperties: false });
export const WorkspaceFileEntrySchema = Type.Object({
    path: Type.String({ minLength: 8, maxLength: 4_105 }),
    name: Type.String({ minLength: 1, maxLength: 255 }),
    kind: Type.Union([Type.Literal("directory"), Type.Literal("file"), Type.Literal("symlink")]),
    size: Type.Union([NonNegativeIntegerSchema, Type.Null()]),
    modifiedAt: Type.Union([TimestampSchema, Type.Null()]),
    mediaType: Type.Union([Type.String({ minLength: 1, maxLength: 255 }), Type.Null()]),
    previewable: Type.Boolean(),
}, { additionalProperties: false });
export const WorkspaceDirectoryListingSchema = Type.Object({
    path: Type.String({ minLength: 8, maxLength: 4_105 }),
    entries: Type.Array(WorkspaceFileEntrySchema, { maxItems: 10_000 }),
    truncated: Type.Boolean(),
}, { additionalProperties: false });
export const WorkspaceFilePreviewSchema = Type.Object({
    path: Type.String({ minLength: 9, maxLength: 4_105 }),
    name: Type.String({ minLength: 1, maxLength: 255 }),
    mediaType: Type.String({ minLength: 1, maxLength: 255 }),
    size: NonNegativeIntegerSchema,
    content: Type.String(),
    truncated: Type.Boolean(),
}, { additionalProperties: false });
export const WorkspaceInputUploadSchema = Type.Object({
    fileRef: FileRefSchema,
    path: Type.String({ minLength: 9, maxLength: 4_105 }),
    name: Type.String({ minLength: 1, maxLength: 255 }),
    size: NonNegativeIntegerSchema,
}, { additionalProperties: false });
export const WorkspaceFileSnapshotSchema = Type.Object({
    path: Type.String({ minLength: 8, maxLength: 4105 }),
    sizeBytes: Type.Integer({ minimum: 0, maximum: 20 * 1024 ** 3 }),
    digest: Sha256Schema,
}, { additionalProperties: false });
export const RuntimeOptionsSchema = Type.Object({
    defaultSeriesId: CatalogIdentifierSchema,
    series: Type.Array(RuntimeSeriesOptionSchema, { minItems: 1, maxItems: 32 }),
    workspaceUpload: WorkspaceUploadPolicySchema,
}, { additionalProperties: false });
export const CreateSessionBodySchema = Type.Object({
    requestId: IdSchema,
    demoSource: Type.Optional(DemoSourceSchema),
    seriesId: CatalogIdentifierSchema,
    start: Type.Union([
        Type.Object({ kind: Type.Literal("draft") }, { additionalProperties: false }),
        Type.Object({
            kind: Type.Literal("turn"),
            goal: Type.String({ minLength: 1, maxLength: 100_000 }),
            planMode: Type.Boolean(),
            guideDefinitionId: Type.Optional(Type.Union([IdSchema, Type.Null()])),
            guideContentDigest: Type.Optional(Type.Union([Sha256Schema, Type.Null()])),
        }, { additionalProperties: false }),
    ]),
}, { additionalProperties: false });
export const UpdateSessionSeriesBodySchema = Type.Object({ seriesId: CatalogIdentifierSchema }, { additionalProperties: false });
export const RenameSessionBodySchema = Type.Object({ title: Type.String({ minLength: 1, maxLength: 120 }) }, { additionalProperties: false });
export const SubmitTurnBodySchema = Type.Object({
    requestId: IdSchema,
    goal: Type.String({ minLength: 1, maxLength: 100_000 }),
    planMode: Type.Optional(Type.Boolean({ default: false })),
    guideDefinitionId: Type.Optional(Type.Union([IdSchema, Type.Null()])),
    imagePaths: Type.Optional(Type.Array(Type.String({ pattern: "^/project/input/", maxLength: 4105 }), {
        maxItems: 4,
        uniqueItems: true,
    })),
}, { additionalProperties: false });
export const SteerTurnBodySchema = SubmitTurnBodySchema;
const TurnResponseProperties = {
    purpose: Type.Optional(Type.Union([Type.Literal("research"), Type.Literal("report"), Type.Literal("guide_review")])),
    termination: Type.Optional(TerminationSchema),
    turnId: IdSchema,
    sessionId: IdSchema,
    requestId: IdSchema,
    generation: Type.Integer({ minimum: 0 }),
    status: Type.Union([
        Type.Literal("accepted"),
        Type.Literal("running"),
        Type.Literal("waiting_plan"),
        Type.Literal("waiting_approval"),
        Type.Literal("completed"),
        Type.Literal("failed"),
        Type.Literal("cancelled"),
    ]),
    guide: Type.Union([SelectedGuideSchema, Type.Null()]),
    createdAt: TimestampSchema,
};
export const TurnSchema = Type.Object(TurnResponseProperties, { additionalProperties: false });
export const SubmittedTurnSchema = Type.Object({
    ...TurnResponseProperties,
    sessionTitle: Type.String({ minLength: 1, maxLength: 120 }),
    sessionTitlePending: Type.Boolean(),
}, { additionalProperties: false });
export const SubmissionSchema = Type.Object({
    requestId: IdSchema,
    session: SessionSchema,
    turn: Type.Union([SubmittedTurnSchema, Type.Null()]),
    acceptedAt: TimestampSchema,
}, { additionalProperties: false });
export const DeletionJobSchema = Type.Object({
    jobId: IdSchema,
    sessionId: IdSchema,
    stage: Type.Union([
        Type.Literal("pending"),
        Type.Literal("authorizations_revoked"),
        Type.Literal("tools_stopped"),
        Type.Literal("tools_absent"),
        Type.Literal("runtime_stopped"),
        Type.Literal("runtime_absent"),
        Type.Literal("tool_storage_deleted"),
        Type.Literal("session_storage_deleted"),
        Type.Literal("database_deleted"),
        Type.Literal("complete"),
    ]),
    attempts: Type.Integer({ minimum: 0 }),
    lastError: Type.Union([Type.String({ maxLength: 2_000 }), Type.Null()]),
    updatedAt: TimestampSchema,
}, { additionalProperties: false });
export const ApproveActionBodySchema = Type.Object({
    requestDigest: Sha256Schema,
    scope: Type.Union([Type.Literal("once"), Type.Literal("session_similar")]),
}, { additionalProperties: false });
export const RevisePlanBodySchema = Type.Object({
    expectedRevision: NonNegativeIntegerSchema,
    steps: Type.Array(Type.Object({ title: Type.String({ minLength: 1, maxLength: 500 }) }, { additionalProperties: false }), { minItems: 1, maxItems: 256 }),
}, { additionalProperties: false });
export const HttpPaths = Object.freeze({
    submission: "/api/submissions/:requestId",
    healthLive: "/health/live",
    healthReady: "/health/ready",
    register: "/api/auth/register",
    authOptions: "/api/auth/options",
    login: "/api/auth/login",
    logout: "/api/auth/logout",
    currentUser: "/api/auth/me",
    completeBootstrap: "/api/auth/complete-bootstrap",
    rawArtifactConsent: "/api/auth/raw-artifact-consent",
    ownPassword: "/api/auth/password",
    adminUsers: "/api/admin/users",
    adminUserActivate: "/api/admin/users/:userId/activate",
    adminUser: "/api/admin/users/:userId",
    adminUserPassword: "/api/admin/users/:userId/password",
    adminUsage: "/api/admin/usage",
    adminModelCacheUsage: "/api/admin/usage/model-cache",
    adminMcpRawUsage: "/api/admin/usage/mcp",
    myUsage: "/api/usage",
    sessionUsage: "/api/sessions/:sessionId/usage",
    runtimeOptions: "/api/runtime/options",
    guides: "/api/guides",
    adminGuideRuntime: "/api/admin/guide-runtime",
    guideRunnerToken: "/api/guide-runner/token",
    adminGuideRunners: "/api/admin/guide-runners",
    adminGuideRunner: "/api/admin/guide-runners/:runnerId",
    adminGuides: "/api/admin/guides",
    adminGuideCanaryTurns: "/api/admin/guides/:definitionId/canary-turns",
    adminGuideCanary: "/api/admin/guides/:definitionId/canaries",
    adminGuideEvidence: "/api/admin/guides/:definitionId/evidence",
    adminGuideReviews: "/api/admin/guides/:definitionId/reviews",
    adminGuideReviewRetry: "/api/admin/guides/:definitionId/reviews/:reviewId/retry",
    adminGuideApproval: "/api/admin/guides/:definitionId/approve",
    sessions: "/api/sessions",
    demoCase: "/api/demos/:caseId",
    demoAsset: "/api/demos/:caseId/assets/:assetId",
    continueDemo: "/api/demos/:caseId/continue",
    session: "/api/sessions/:sessionId",
    sessionSeries: "/api/sessions/:sessionId/series",
    sessionReport: "/api/sessions/:sessionId/report",
    retryReport: "/api/sessions/:sessionId/report/retry",
    workspaceFiles: "/api/sessions/:sessionId/workspace/files",
    workspacePreview: "/api/sessions/:sessionId/workspace/preview",
    workspaceUpload: "/api/sessions/:sessionId/workspace/upload",
    workspaceFileVersions: "/api/sessions/:sessionId/workspace/file-versions",
    workspaceFileSnapshot: "/api/sessions/:sessionId/workspace/file-snapshot",
    workspaceInput: "/api/sessions/:sessionId/workspace/input",
    workspaceDownload: "/api/sessions/:sessionId/workspace/download",
    workspaceContent: "/api/sessions/:sessionId/workspace/content",
    turns: "/api/sessions/:sessionId/turns",
    steerTurn: "/api/sessions/:sessionId/steer",
    turn: "/api/turns/:turnId",
    termination: "/api/terminations/:terminationId",
    adminTerminations: "/api/admin/terminations",
    terminationRetry: "/api/terminations/:terminationId/retry",
    terminationResolve: "/api/terminations/:terminationId/resolve",
    events: "/api/sessions/:sessionId/events",
    plan: "/api/turns/:turnId/plan",
    turnAuthorization: "/api/turns/:turnId/authorization",
    revisePlan: "/api/turns/:turnId/plan",
    startTurn: "/api/turns/:turnId/start",
    actionDetails: "/api/actions",
    actions: "/api/actions/:actionId",
    actionGrant: "/api/actions/:actionId/grant",
    actionObservation: "/api/actions/:actionId/observation",
    approveAction: "/api/actions/:actionId/approve",
    deletion: "/api/deletions/:jobId",
    modelProxyResponses: "/internal/model-proxy/responses",
    modelProxyWebSearch: "/internal/model-proxy/web-search",
});
export const ProtocolCompatibility = Object.freeze({
    digestQueryParameter: "protocolDigest",
    digestResponseHeader: "x-rnavoyager-protocol-digest",
});
const EmptySchema = Type.Null();
const StatusSchema = Type.Object({ status: Type.String() }, { additionalProperties: false });
const SessionParamsSchema = Type.Object({ sessionId: IdSchema }, { additionalProperties: false });
const TurnParamsSchema = Type.Object({ turnId: IdSchema }, { additionalProperties: false });
const ActionParamsSchema = Type.Object({ actionId: IdSchema }, { additionalProperties: false });
/**
 * 动作详情批量读取：`ids` 为逗号分隔的动作 ID，单次上限为 ACTION_DETAILS_BATCH_LIMIT。
 */
export const ACTION_DETAILS_BATCH_LIMIT = 100;
export const ActionDetailsQuerySchema = Type.Object({ ids: Type.String({ minLength: 1, maxLength: 8_192 }) }, { additionalProperties: false });
export const ActionDetailsEntrySchema = Type.Object({
    actionId: IdSchema,
    request: ActionRequestSchema,
    grant: Type.Union([ActionGrantSchema, Type.Null()]),
}, { additionalProperties: false });
export const ActionDetailsListSchema = Type.Array(ActionDetailsEntrySchema);
const GuideParamsSchema = Type.Object({ definitionId: IdSchema }, { additionalProperties: false });
const GuideReviewParamsSchema = Type.Object({ definitionId: IdSchema, reviewId: IdSchema }, { additionalProperties: false });
const UserParamsSchema = Type.Object({ userId: IdSchema }, { additionalProperties: false });
const DeletionParamsSchema = Type.Object({ jobId: IdSchema }, { additionalProperties: false });
const EventQuerySchema = Type.Object({
    after: Type.Optional(Type.String({ pattern: "^(0|[1-9][0-9]*)$", maxLength: 19 })),
    [ProtocolCompatibility.digestQueryParameter]: Type.Optional(Sha256Schema),
}, { additionalProperties: false });
export const PlanQuerySchema = Type.Object({ revision: Type.Optional(NonNegativeIntegerSchema) }, { additionalProperties: false });
const WorkspacePathQuerySchema = Type.Object({ path: Type.String({ minLength: 8, maxLength: 4_105 }) }, { additionalProperties: false });
const WorkspaceUploadQuerySchema = Type.Object({ filename: Type.String({ minLength: 1, maxLength: 255 }) }, { additionalProperties: false });
export const httpRouteContracts = Object.freeze({
    healthLive: {
        protocolNumber: 1,
        method: "GET",
        path: HttpPaths.healthLive,
        response: { 200: StatusSchema },
    },
    healthReady: {
        protocolNumber: 1,
        method: "GET",
        path: HttpPaths.healthReady,
        response: { 200: StatusSchema },
    },
    register: {
        protocolNumber: 5,
        method: "POST",
        path: HttpPaths.register,
        body: RegisterBodySchema,
        response: { 201: PublicUserSchema },
    },
    authOptions: {
        protocolNumber: 5,
        method: "GET",
        path: HttpPaths.authOptions,
        response: { 200: AuthOptionsSchema },
    },
    login: {
        protocolNumber: 5,
        method: "POST",
        path: HttpPaths.login,
        body: LoginBodySchema,
        response: { 200: PublicUserSchema },
    },
    logout: {
        protocolNumber: 5,
        method: "POST",
        path: HttpPaths.logout,
        response: { 204: EmptySchema },
    },
    currentUser: {
        protocolNumber: 5,
        method: "GET",
        path: HttpPaths.currentUser,
        response: { 200: PublicUserSchema },
    },
    completeBootstrap: {
        protocolNumber: 5,
        method: "POST",
        path: HttpPaths.completeBootstrap,
        body: CompleteBootstrapBodySchema,
        response: { 200: PublicUserSchema },
    },
    rawArtifactConsent: {
        protocolNumber: 5,
        method: "POST",
        path: HttpPaths.rawArtifactConsent,
        body: UpdateRawArtifactConsentBodySchema,
        response: { 200: PublicUserSchema },
    },
    ownPassword: {
        protocolNumber: 5,
        method: "PATCH",
        path: HttpPaths.ownPassword,
        body: ChangeOwnPasswordBodySchema,
        response: { 200: PublicUserSchema },
    },
    adminUsersList: {
        protocolNumber: 5,
        method: "GET",
        path: HttpPaths.adminUsers,
        response: { 200: AdminUserListSchema },
    },
    adminUserActivate: {
        protocolNumber: 5,
        method: "POST",
        path: HttpPaths.adminUserActivate,
        params: UserParamsSchema,
        response: { 200: AdminUserSchema },
    },
    adminUserPassword: {
        protocolNumber: 5,
        method: "PATCH",
        path: HttpPaths.adminUserPassword,
        params: UserParamsSchema,
        body: AdminResetUserPasswordBodySchema,
        response: { 200: AdminUserSchema },
    },
    adminUserDelete: {
        protocolNumber: 5,
        method: "DELETE",
        path: HttpPaths.adminUser,
        params: UserParamsSchema,
        response: { 204: EmptySchema },
    },
    adminUsage: {
        protocolNumber: 2,
        method: "GET",
        path: HttpPaths.adminUsage,
        response: { 200: AdminUsageListSchema },
    },
    adminModelCacheUsage: {
        protocolNumber: 3,
        method: "GET",
        path: HttpPaths.adminModelCacheUsage,
        querystring: ModelCacheUsageQuerySchema,
        response: { 200: ModelCacheUsageSnapshotSchema },
    },
    adminMcpRawUsage: {
        protocolNumber: 2,
        method: "GET",
        path: HttpPaths.adminMcpRawUsage,
        response: { 200: McpRawUsageSchema },
    },
    myUsage: {
        protocolNumber: 2,
        method: "GET",
        path: HttpPaths.myUsage,
        response: { 200: AdminUserUsageSchema },
    },
    sessionUsage: {
        protocolNumber: 2,
        method: "GET",
        path: HttpPaths.sessionUsage,
        params: SessionParamsSchema,
        response: { 200: SessionUsageSummarySchema },
    },
    runtimeOptions: {
        protocolNumber: 3,
        method: "GET",
        path: HttpPaths.runtimeOptions,
        response: { 200: RuntimeOptionsSchema },
    },
    guides: {
        protocolNumber: 7,
        method: "GET",
        path: HttpPaths.guides,
        response: { 200: GuideListSchema },
    },
    adminGuideList: {
        protocolNumber: 7,
        method: "GET",
        path: HttpPaths.adminGuides,
        response: { 200: AdminGuideListSchema },
    },
    guideRunnerToken: {
        protocolNumber: 7,
        method: "POST",
        path: HttpPaths.guideRunnerToken,
        body: GuideRunnerTokenBodySchema,
        response: { 200: GuideRunnerTokenSchema },
    },
    adminGuideRunners: {
        protocolNumber: 7,
        method: "GET",
        path: HttpPaths.adminGuideRunners,
        response: { 200: Type.Array(GuideRunnerSchema) },
    },
    adminGuideRunnerRevoke: {
        protocolNumber: 7,
        method: "DELETE",
        path: HttpPaths.adminGuideRunner,
        params: Type.Object({ runnerId: IdSchema }, { additionalProperties: false }),
        response: { 204: EmptySchema },
    },
    adminGuideRuntime: {
        protocolNumber: 7,
        method: "GET",
        path: HttpPaths.adminGuideRuntime,
        response: { 200: GuideRuntimeSnapshotSchema },
    },
    adminGuides: {
        protocolNumber: 7,
        method: "POST",
        path: HttpPaths.adminGuides,
        body: GuideDefinitionSchema,
        response: { 201: GuideDefinitionSchema },
    },
    adminGuideCanaryTurns: {
        protocolNumber: 7,
        method: "POST",
        path: HttpPaths.adminGuideCanaryTurns,
        params: GuideParamsSchema,
        body: CreateGuideCanaryTurnBodySchema,
        response: { 202: TurnSchema },
    },
    adminGuideCanary: {
        protocolNumber: 7,
        method: "POST",
        path: HttpPaths.adminGuideCanary,
        params: GuideParamsSchema,
        body: RecordGuideCanaryBodySchema,
        response: { 200: GuideLifecycleResultSchema },
    },
    adminGuideEvidence: {
        protocolNumber: 7,
        method: "GET",
        path: HttpPaths.adminGuideEvidence,
        params: GuideParamsSchema,
        response: { 200: GuideReviewEvidenceSchema },
    },
    adminGuideReviews: {
        protocolNumber: 7,
        method: "GET",
        path: HttpPaths.adminGuideReviews,
        params: GuideParamsSchema,
        response: { 200: GuideReviewSummaryListSchema },
    },
    adminGuideReviewRetry: {
        protocolNumber: 7,
        method: "POST",
        path: HttpPaths.adminGuideReviewRetry,
        params: GuideReviewParamsSchema,
        body: Type.Object({}, { additionalProperties: false }),
        response: { 202: GuideReviewRetryResultSchema },
    },
    adminGuideApproval: {
        protocolNumber: 7,
        method: "POST",
        path: HttpPaths.adminGuideApproval,
        params: GuideParamsSchema,
        body: ApproveGuideBodySchema,
        response: { 200: GuideLifecycleResultSchema },
    },
    sessionsList: {
        protocolNumber: 1,
        method: "GET",
        path: HttpPaths.sessions,
        response: { 200: SessionListSchema },
    },
    demoCase: {
        protocolNumber: 1,
        method: "GET",
        path: HttpPaths.demoCase,
        params: Type.Object({ caseId: DemoIdSchema }, { additionalProperties: false }),
        response: { 200: DemoCaseResponseSchema },
    },
    demoAsset: {
        protocolNumber: 1,
        method: "GET",
        path: HttpPaths.demoAsset,
        params: Type.Object({ caseId: DemoIdSchema, assetId: DemoIdSchema }, { additionalProperties: false }),
        querystring: Type.Object({ caseDigest: Sha256Schema, download: Type.Optional(Type.Literal("1")) }, { additionalProperties: false }),
        response: { 200: Type.String({ format: "binary" }) },
    },
    continueDemo: {
        protocolNumber: 1,
        method: "POST",
        path: HttpPaths.continueDemo,
        params: Type.Object({ caseId: DemoIdSchema }, { additionalProperties: false }),
        body: ContinueDemoBodySchema,
        response: {
            200: Type.Object({
                state: Type.Union([Type.Literal("preparing"), Type.Literal("ready")]),
                sessionId: IdSchema,
                files: Type.Array(WorkspaceInputUploadSchema, { maxItems: 129 }),
                context: Type.String({ maxLength: 64_000 }),
            }, { additionalProperties: false }),
        },
    },
    sessions: {
        protocolNumber: 1,
        method: "POST",
        path: HttpPaths.sessions,
        body: CreateSessionBodySchema,
        response: { 202: SubmissionSchema },
    },
    submission: {
        protocolNumber: 1,
        method: "GET",
        path: HttpPaths.submission,
        params: Type.Object({ requestId: IdSchema }, { additionalProperties: false }),
        response: { 200: SubmissionSchema },
    },
    session: {
        protocolNumber: 1,
        method: "DELETE",
        path: HttpPaths.session,
        params: SessionParamsSchema,
        response: { 202: DeletionJobSchema },
    },
    sessionGet: {
        protocolNumber: 1,
        method: "GET",
        path: HttpPaths.session,
        params: SessionParamsSchema,
        response: { 200: SessionSchema },
    },
    sessionRename: {
        protocolNumber: 1,
        method: "PATCH",
        path: HttpPaths.session,
        params: SessionParamsSchema,
        body: RenameSessionBodySchema,
        response: { 200: SessionSchema },
    },
    sessionSeries: {
        protocolNumber: 1,
        method: "PATCH",
        path: HttpPaths.sessionSeries,
        params: SessionParamsSchema,
        body: UpdateSessionSeriesBodySchema,
        response: { 200: SessionSchema },
    },
    sessionReport: {
        protocolNumber: 1,
        method: "GET",
        path: HttpPaths.sessionReport,
        params: SessionParamsSchema,
        response: { 200: Type.Union([SessionReportSchema, Type.Null()]) },
    },
    retryReport: {
        protocolNumber: 1,
        method: "POST",
        path: HttpPaths.retryReport,
        params: SessionParamsSchema,
        body: RetryReportBodySchema,
        response: { 202: TurnSchema },
    },
    workspaceFiles: {
        protocolNumber: 6,
        method: "GET",
        path: HttpPaths.workspaceFiles,
        params: SessionParamsSchema,
        querystring: WorkspacePathQuerySchema,
        response: { 200: WorkspaceDirectoryListingSchema },
    },
    workspaceFileVersions: {
        protocolNumber: 6,
        method: "GET",
        path: HttpPaths.workspaceFileVersions,
        params: SessionParamsSchema,
        response: {
            200: Type.Object({ files: Type.Array(FileVersionSchema, { maxItems: 4096 }) }, { additionalProperties: false }),
        },
    },
    workspaceVerifyFile: {
        protocolNumber: 6,
        method: "POST",
        path: HttpPaths.workspaceFileVersions,
        params: SessionParamsSchema,
        body: Type.Object({ path: Type.String({ minLength: 1, maxLength: 4105 }), expectedDigest: Sha256Schema }, { additionalProperties: false }),
        response: { 201: FileVersionSchema },
    },
    workspaceFileSnapshot: {
        protocolNumber: 6,
        method: "GET",
        path: HttpPaths.workspaceFileSnapshot,
        params: SessionParamsSchema,
        querystring: WorkspacePathQuerySchema,
        response: { 200: WorkspaceFileSnapshotSchema },
    },
    workspacePreview: {
        protocolNumber: 6,
        method: "GET",
        path: HttpPaths.workspacePreview,
        params: SessionParamsSchema,
        querystring: WorkspacePathQuerySchema,
        response: { 200: WorkspaceFilePreviewSchema },
    },
    workspaceUpload: {
        protocolNumber: 6,
        method: "POST",
        path: HttpPaths.workspaceUpload,
        params: SessionParamsSchema,
        querystring: WorkspaceUploadQuerySchema,
        body: Type.Unknown(),
        response: { 201: WorkspaceInputUploadSchema },
    },
    workspaceInput: {
        protocolNumber: 6,
        method: "DELETE",
        path: HttpPaths.workspaceInput,
        params: SessionParamsSchema,
        querystring: WorkspacePathQuerySchema,
        response: { 204: EmptySchema },
    },
    workspaceDownload: {
        protocolNumber: 6,
        method: "GET",
        path: HttpPaths.workspaceDownload,
        params: SessionParamsSchema,
        querystring: WorkspacePathQuerySchema,
        response: { 200: Type.Unknown() },
    },
    workspaceContent: {
        protocolNumber: 6,
        method: "GET",
        path: HttpPaths.workspaceContent,
        params: SessionParamsSchema,
        querystring: WorkspacePathQuerySchema,
        response: { 200: Type.Unknown() },
    },
    turns: {
        protocolNumber: 1,
        method: "POST",
        path: HttpPaths.turns,
        params: SessionParamsSchema,
        body: SubmitTurnBodySchema,
        response: { 202: SubmittedTurnSchema },
    },
    steerTurn: {
        protocolNumber: 1,
        method: "POST",
        path: HttpPaths.steerTurn,
        params: SessionParamsSchema,
        body: SteerTurnBodySchema,
        response: { 202: SubmittedTurnSchema },
    },
    turn: {
        protocolNumber: 1,
        method: "DELETE",
        path: HttpPaths.turn,
        params: TurnParamsSchema,
        response: { 202: TurnSchema },
    },
    termination: {
        protocolNumber: 1,
        method: "GET",
        path: HttpPaths.termination,
        params: Type.Object({ terminationId: IdSchema }, { additionalProperties: false }),
        response: { 200: TerminationSchema },
    },
    adminTerminations: {
        protocolNumber: 1,
        method: "GET",
        path: HttpPaths.adminTerminations,
        response: { 200: Type.Array(TerminationSchema) },
    },
    terminationRetry: {
        protocolNumber: 1,
        method: "POST",
        path: HttpPaths.terminationRetry,
        params: Type.Object({ terminationId: IdSchema }, { additionalProperties: false }),
        response: { 202: TerminationSchema },
    },
    terminationResolve: {
        protocolNumber: 1,
        method: "POST",
        path: HttpPaths.terminationResolve,
        params: Type.Object({ terminationId: IdSchema }, { additionalProperties: false }),
        body: Type.Object({
            grantId: IdSchema,
            resourceId: Type.Union([IdSchema, Type.Null()]),
            evidenceDigest: Sha256Schema,
        }, { additionalProperties: false }),
        response: { 202: TerminationSchema },
    },
    events: {
        protocolNumber: 1,
        method: "GET",
        path: HttpPaths.events,
        params: SessionParamsSchema,
        querystring: EventQuerySchema,
        responseHeaders: { [ProtocolCompatibility.digestResponseHeader]: Sha256Schema },
        response: { 200: TaskEventListSchema },
    },
    plan: {
        protocolNumber: 1,
        method: "GET",
        path: HttpPaths.plan,
        params: TurnParamsSchema,
        querystring: PlanQuerySchema,
        response: { 200: WorkingPlanSchema },
    },
    turnAuthorization: {
        protocolNumber: 2,
        method: "GET",
        path: HttpPaths.turnAuthorization,
        params: TurnParamsSchema,
        response: { 200: AuthorizationEnvelopeSchema },
    },
    revisePlan: {
        protocolNumber: 1,
        method: "PATCH",
        path: HttpPaths.revisePlan,
        params: TurnParamsSchema,
        body: RevisePlanBodySchema,
        response: { 200: WorkingPlanSchema },
    },
    startTurn: {
        protocolNumber: 1,
        method: "POST",
        path: HttpPaths.startTurn,
        params: TurnParamsSchema,
        response: { 202: TurnSchema },
    },
    actions: {
        protocolNumber: 2,
        method: "GET",
        path: HttpPaths.actions,
        params: ActionParamsSchema,
        response: { 200: ActionRequestSchema },
    },
    actionDetails: {
        protocolNumber: 2,
        method: "GET",
        path: HttpPaths.actionDetails,
        querystring: ActionDetailsQuerySchema,
        response: { 200: ActionDetailsListSchema },
    },
    actionGrant: {
        protocolNumber: 2,
        method: "GET",
        path: HttpPaths.actionGrant,
        params: ActionParamsSchema,
        response: { 200: ActionGrantSchema },
    },
    actionObservation: {
        protocolNumber: 2,
        method: "GET",
        path: HttpPaths.actionObservation,
        params: ActionParamsSchema,
        response: { 200: ObservationSchema },
    },
    approveAction: {
        protocolNumber: 2,
        method: "POST",
        path: HttpPaths.approveAction,
        params: ActionParamsSchema,
        body: ApproveActionBodySchema,
        response: { 200: ActionGrantSchema },
    },
    deletion: {
        protocolNumber: 1,
        method: "GET",
        path: HttpPaths.deletion,
        params: DeletionParamsSchema,
        response: { 200: DeletionJobSchema },
    },
    modelProxyResponses: {
        protocolNumber: 3,
        method: "POST",
        path: HttpPaths.modelProxyResponses,
        body: ModelProxyRequestSchema,
        response: {
            200: Type.Unknown(),
            410: ModelProxyExpiredSchema,
            503: ModelProxyStorageFailureSchema,
        },
    },
    modelProxyWebSearch: {
        protocolNumber: 8,
        method: "POST",
        path: HttpPaths.modelProxyWebSearch,
        body: ProviderSearchProxyRequestSchema,
        response: { 200: ProviderSearchProxyResponseSchema },
    },
});
//# sourceMappingURL=http.js.map